import {
    fillGDIResponsiveInheritance,
    hasGDIViewTypes,
    parseGDIViewTypes,
    resolveFontStyleProps,
    resolveGDIStyles,
    resolveResponsiveFontStyleProps
} from '../gdi/utils/gdi-styles'

/* Mirrors CTA_STYLE_MAP from gdiCtaBlock — used to translate authored CMS
 * style fields on a Mega-Menu-Link CTA into Chakra props that override the
 * navigation's default styling. */
const MEGA_MENU_LINK_STYLE_MAP = {
    Color: 'color',
    BackgroundColor: 'backgroundColor',
    BorderColor: 'borderColor',
    MinWidth: 'minWidth',
    GDICtaBlockPadding: 'padding',
    Margin: 'margin'
}

const isDesktopHoverEntry = (entry) => parseGDIViewTypes(entry?.ViewTypesToApply).includes('_hover')

const getPrimaryCtaStyleEntry = (stylesList) => {
    if (!Array.isArray(stylesList)) return {}
    return (
        stylesList.find((entry) => !hasGDIViewTypes(entry) && entry?.Style) ||
        stylesList.find((entry) => !isDesktopHoverEntry(entry) && entry?.Style) ||
        stylesList.find((entry) => entry?.Style) ||
        {}
    )
}

/**
 * Resolve the CtaText for the active layout from a GDICtaBlockStylesPerViewType
 * list. Each entry's `ViewTypesToApply` is a comma-separated list of web view
 * tokens (e.g. "WebMobile,WebTablet"). When `currentViewType` is provided we
 * return the CtaText of the entry that targets it; otherwise (or when no entry
 * matches it) we fall back to the first authored CtaText so callers without a
 * view-type context keep their previous behaviour.
 */
export const resolveCtaTextForViewType = (stylesList, currentViewType) => {
    if (!Array.isArray(stylesList)) return ''
    if (currentViewType) {
        const match = stylesList.find((entry) => {
            if (!entry?.CtaText) return false
            return String(entry.ViewTypesToApply || '')
                .split(',')
                .map((token) => token.trim())
                .filter(Boolean)
                .includes(currentViewType)
        })
        if (match) return match.CtaText
    }
    return stylesList.find((entry) => entry?.CtaText)?.CtaText || ''
}

function deepMerge(target, source) {
    for (const key of Object.keys(source)) {
        if (source[key] instanceof Object && key in target) {
            target[key] = deepMerge(target[key], source[key]);
        } else {
            target[key] = source[key];
        }
    }
    return target;
}

const mapHeadingToFontSize = (value, headingOverrides = {}) => {
    if (!value) return value;

    const defaultHeadingMap = {
        'h1': { base: '20px', lg: '24px' },
        'h2': { base: '16px', lg: '20px' },
        'h3': { base: '14px', lg: '16px' },
        'h4': { base: '12px', lg: '12px' },
        'h5': { base: '14px', lg: '14px' },
        'h6': { base: '12px', lg: '12px' }
    };

    // Deep merge default mappings with overrides
    const headingMap = deepMerge(defaultHeadingMap, headingOverrides);

    // Check if it's a heading tag (case-insensitive)
    const lowerValue = value.toString().toLowerCase();
    if (headingMap.hasOwnProperty(lowerValue)) {
        return headingMap[lowerValue];
    }

    // If not a heading tag, return the original value (could be xs, sm, md, lg, xl, 2xl, etc.)
    return value;
};

const mapFontFamily = (value) => {
    const mapping = {
        'Garage Neue': 'GarageNeue'
    }

    return mapping[value]
}

const mapFontStyle = (value) => {
    const mapping = {
        'bold': 'bold',
        'medium': 'medium',
        'roman': 'normal',
        'normal': 'normal',
        'italic': 'italic'
    };

    return mapping[value?.toLowerCase()] || mapping[value]
}

/**
 * Converts a styling configuration object to Chakra UI style props
 * @param {Object} styleConfig - The styling configuration object
 * @param {Object} fallback - The fallback styling configuration object
 * @param {Object} headingOverrides - Custom heading size mappings (e.g., { h1: '48px', h2: '36px' })
 * @returns {Object} Chakra UI compatible style object
 */
export const generateStyles = (styleConfig, fallback = {}, headingOverrides = {}) => {
    const styles = {};

    // Helper function to get fallback value for a given property and breakpoint
    const getFallbackValue = (property, breakpoint = null) => {
        if (!fallback || typeof fallback !== 'object') return null;

        const fallbackValue = fallback[property];
        if (!fallbackValue) return null;

        if (breakpoint && typeof fallbackValue === 'object' && fallbackValue[breakpoint]) {
            return fallbackValue[breakpoint];
        } else if (!breakpoint && typeof fallbackValue === 'string') {
            return fallbackValue;
        } else if (!breakpoint && typeof fallbackValue === 'object' && fallbackValue.base) {
            return fallbackValue.base;
        }

        return null;
    };

    // Helper function to add property if value exists, with fallback support
    const addIfExists = (chakraProperty, value, breakpoint = null, configProperty = null) => {
        // Use the configProperty name for fallback lookup if provided, otherwise use chakraProperty
        const fallbackProperty = configProperty || chakraProperty;

        // Check if we have a value from styleConfig
        const hasValue = value && value.toString().trim() !== '';

        // Get fallback value if main value doesn't exist
        const fallbackValue = !hasValue ? getFallbackValue(fallbackProperty, breakpoint) : null;

        // Use main value if it exists, otherwise use fallback
        const finalValue = hasValue ? value : fallbackValue;

        if (finalValue && finalValue.toString().trim() !== '') {
            if (breakpoint) {
                if (!styles[chakraProperty]) {
                    styles[chakraProperty] = {};
                }
                // If the property was previously set as a string, convert it to an object
                if (typeof styles[chakraProperty] === 'string') {
                    const baseValue = styles[chakraProperty];
                    styles[chakraProperty] = { base: baseValue };
                }
                styles[chakraProperty][breakpoint] = finalValue;
            } else {
                // Only set as string if no breakpoint object exists yet
                if (!styles[chakraProperty]) {
                    styles[chakraProperty] = finalValue;
                } else if (typeof styles[chakraProperty] === 'object') {
                    styles[chakraProperty].base = finalValue;
                }
            }
        }
    };

    // Base (mobile) styles
    addIfExists('fontFamily', mapFontFamily(styleConfig?.FontFamily));
    addIfExists('fontWeight', mapFontStyle(styleConfig?.FontStyle));
    addIfExists('color', styleConfig?.TextColorHex || styleConfig?.TextColor);
    addIfExists('fontSize', styleConfig?.TextSizePt ? `${styleConfig?.TextSizePt}px` : mapHeadingToFontSize(styleConfig?.TextSize, headingOverrides));
    addIfExists('textAlign', styleConfig?.TextAlignment);

    // Tablet styles
    addIfExists('fontFamily', mapFontFamily(styleConfig?.FontFamilyTablet), 'md');
    addIfExists('fontWeight', mapFontStyle(styleConfig?.FontStyleTablet), 'md');
    addIfExists('color', styleConfig?.TextColorHexTablet || styleConfig?.TextColorTablet, 'md');
    addIfExists('fontSize', styleConfig?.TextSizePtTablet ? `${styleConfig?.TextSizePtTablet}px` : mapHeadingToFontSize(styleConfig?.TextSizeTablet, headingOverrides), 'md');
    addIfExists('textAlign', styleConfig?.TextAlignmentTablet, 'md');

    // Desktop styles
    addIfExists('fontFamily', mapFontFamily(styleConfig?.FontFamilyDesktop), 'lg');
    addIfExists('fontWeight', mapFontStyle(styleConfig?.FontStyleDesktop), 'lg');
    addIfExists('color', styleConfig?.TextColorHexDesktop || styleConfig?.TextColorDesktop, 'lg');
    addIfExists('fontSize', styleConfig?.TextSizePtDesktop ? `${styleConfig?.TextSizePtDesktop}px` : mapHeadingToFontSize(styleConfig?.TextSizeDesktop, headingOverrides), 'lg');
    addIfExists('textAlign', styleConfig?.TextAlignmentDesktop, 'lg');

    // Pass through any non-CMS (Chakra-compatible) keys from styleConfig
    const CMS_KEYS = new Set([
        'FontFamily', 'FontStyle', 'TextColorHex', 'TextColor', 'TextSizePt', 'TextSize', 'TextAlignment',
        'FontFamilyTablet', 'FontStyleTablet', 'TextColorHexTablet', 'TextColorTablet', 'TextSizePtTablet', 'TextSizeTablet', 'TextAlignmentTablet',
        'FontFamilyDesktop', 'FontStyleDesktop', 'TextColorHexDesktop', 'TextColorDesktop', 'TextSizePtDesktop', 'TextSizeDesktop', 'TextAlignmentDesktop'
    ])
    if (styleConfig && typeof styleConfig === 'object') {
        Object.keys(styleConfig).forEach(property => {
            if (CMS_KEYS.has(property)) return
            const value = styleConfig[property]
            if (typeof value === 'object') {
                styles[property] = { ...value }
            } else {
                styles[property] = value
            }
        })
    }

    // Check for any fallback properties that weren't handled above
    if (fallback && typeof fallback === 'object') {
        Object.keys(fallback).forEach(property => {
            const fallbackValue = fallback[property];

            // Skip if this property was already processed
            if (styles[property]) return;

            // Add the fallback property
            if (typeof fallbackValue === 'object') {
                styles[property] = { ...fallbackValue };
            } else {
                styles[property] = fallbackValue;
            }
        });
    }

    return styles;
};

// Alternative version that uses arrays for responsive values
export const generateStylesArray = (styleConfig, fallback = {}, headingOverrides = {}) => {
    const getValue = (base, tablet, desktop, transform = (v) => v, property = null) => {
        const values = [base, tablet, desktop].map(v =>
            v && v.toString().trim() !== '' ? transform(v) : null
        );

        // Check fallback for missing values
        if (fallback && property && fallback[property]) {
            const fallbackValue = fallback[property];
            if (Array.isArray(fallbackValue)) {
                for (let i = 0; i < Math.min(values.length, fallbackValue.length); i++) {
                    if (values[i] === null && fallbackValue[i] !== null && fallbackValue[i] !== undefined) {
                        values[i] = fallbackValue[i];
                    }
                }
            } else if (typeof fallbackValue === 'object') {
                if (values[0] === null && fallbackValue.base) values[0] = fallbackValue.base;
                if (values[1] === null && fallbackValue.md) values[1] = fallbackValue.md;
                if (values[2] === null && fallbackValue.lg) values[2] = fallbackValue.lg;
            } else if (typeof fallbackValue === 'string' && values[0] === null) {
                values[0] = fallbackValue;
            }
        }

        // Remove trailing nulls
        while (values.length > 1 && values[values.length - 1] === null) {
            values.pop();
        }

        // Return single value if only base exists, array if multiple values
        return values.length === 1 ? values[0] : values.some(v => v !== null) ? values : null;
    };

    const styles = {};

    // Font Family
    const fontFamily = getValue(styleConfig.FontFamily, styleConfig.FontFamilyTablet, styleConfig.FontFamilyDesktop, v => v, 'fontFamily');
    if (fontFamily) styles.fontFamily = fontFamily;

    // Font Style (using fontWeight for bold, italic, etc.)
    const fontStyle = getValue(styleConfig.FontStyle, styleConfig.FontStyleTablet, styleConfig.FontStyleDesktop, v => v, 'fontWeight');
    if (fontStyle) {
        // Map common font styles to fontWeight
        const styleMap = {
            'bold': 'bold',
            'medium': 'medium',
            'normal': 'normal',
            'italic': 'italic'
        };

        const mappedStyle = Array.isArray(fontStyle) ?
            fontStyle.map(s => styleMap[s] || s) :
            (styleMap[fontStyle] || fontStyle);

        // If it's italic, use fontStyle, otherwise use fontWeight
        if (mappedStyle === 'italic' || (Array.isArray(mappedStyle) && mappedStyle.includes('italic'))) {
            styles.fontStyle = fontStyle;
        } else {
            styles.fontWeight = fontStyle;
        }
    }

    // Color (prioritize hex over regular color)
    const color = getValue(
        styleConfig?.TextColorHex || styleConfig?.TextColor,
        styleConfig?.TextColorHexTablet || styleConfig?.TextColorTablet,
        styleConfig?.TextColorHexDesktop || styleConfig?.TextColorDesktop,
        v => v,
        'color'
    );
    if (color) styles.color = color;

    // Font Size (prioritize pt values, map heading tags, preserve direct values)
    const fontSize = getValue(
        styleConfig.TextSizePt ? `${styleConfig.TextSizePt}px` : mapHeadingToFontSize(styleConfig.TextSize, headingOverrides),
        styleConfig.TextSizePtTablet ? `${styleConfig.TextSizePtTablet}px` : mapHeadingToFontSize(styleConfig.TextSizeTablet, headingOverrides),
        styleConfig.TextSizePtDesktop ? `${styleConfig.TextSizePtDesktop}px` : mapHeadingToFontSize(styleConfig.TextSizeDesktop, headingOverrides),
        v => v,
        'fontSize'
    );
    if (fontSize) styles.fontSize = fontSize;

    // Text Alignment
    const textAlign = getValue(styleConfig.TextAlignment, styleConfig.TextAlignmentTablet, styleConfig.TextAlignmentDesktop, v => v, 'textAlign');
    if (textAlign) styles.textAlign = textAlign;

    // Add any remaining fallback properties
    if (fallback && typeof fallback === 'object') {
        Object.keys(fallback).forEach(property => {
            if (!styles[property]) {
                styles[property] = fallback[property];
            }
        });
    }

    return styles;
};

export const CMS_MEGA_MENU_NO_MENU = 'NoMenu';

/**
 * True when the CMS MegaMenu config for `categoryId` has ListOfLinksSource = 'NoMenu',
 * meaning the category renders as a plain link with no mega menu (and no subcategory fetch).
 *
 * @param {string} categoryId
 * @param {Object} menuData - CMS global navigation data (with `MegaMenus`).
 * @returns {boolean}
 */
export const isCmsMegaMenuDisabled = (categoryId, menuData) => {
    if (!categoryId || !Array.isArray(menuData?.MegaMenus)) return false;
    return menuData.MegaMenus.some(
        (m) => m?.categoryId === categoryId && m.listOfLinksSource === CMS_MEGA_MENU_NO_MENU
    );
}

/**
* Merges CMS-driven category replacement and shop menu content data into a main category object.
*
* For the given main category and menu data, this function searches for matching category replacements
* and shop menu content (by CategoryId) and attaches them to the corresponding categories and subcategories
* within the main category structure. The function returns a new merged category object with the additional
* CMS data applied where matches are found.
*
* Supports multiple block types in CategoryReplacement:
* - CustomCategoryBlock: Simple category overrides (name, color, badge)
* - MegaMenuItemLevelOneBlock: Complete mega menu structure replacement
*
* @param {Object} mainCategory - The main category object to be enriched with CMS data.
* @param {Object} menuData - The CMS menu data containing CategoryReplacement and ShopMenuContent arrays.
* @returns {Object} The merged category object with CMS replacements and shop menu content applied.
*/
export const mergeCMSCategoryReplacements = (mainCategory, menuData) => {
    if (!mainCategory || !menuData) return mainCategory;

    const merged = { ...mainCategory };

    const categoryReplacements = menuData.CategoryReplacement || [];
    const shopMenuContent = menuData.ShopMenuContent || [];

    // ── CMS mega menu config (per-category MegaMenu entries with own CustomListOfLinks / ContentTile.Illustrations) ──
    const megaMenuConfigs = Array.isArray(menuData.MegaMenus) ? menuData.MegaMenus : [];

    const mergeToCategory = (category) => {
        const categoryId = category.id;

        // Find matching replacement - can be CustomCategoryBlock or MegaMenuItemLevelOneBlock
        const matchingReplacement = categoryReplacements.find(item =>
            item.ContentLink?.Expanded?.CategoryId === categoryId
        );

        if (matchingReplacement) {
            const expanded = matchingReplacement.ContentLink?.Expanded;
            const blockType = expanded?.__typename;

            category.CategoryReplacement = matchingReplacement;

            // If it's a MegaMenuItemLevelOneBlock, mark category as having CMS mega menu structure
            if (blockType === 'MegaMenuItemLevelOneBlock') {
                category.hasCMSMegaMenu = true;
                category.CMSMegaMenuData = expanded;
            }
        }

        const matchingShopContent = shopMenuContent.find(item =>
            item.ContentLink?.Expanded?.CategoryId === categoryId
        );

        if (matchingShopContent) {
            category.ShopMenuContent = matchingShopContent;

            // Extract MegaMenuIlustrationBlockList from ShopMenuContent for non-CMS mega menus
            const shopContentExpanded = matchingShopContent.ContentLink?.Expanded;
            if (shopContentExpanded?.MegaMenuIlustrationBlockList?.length > 0) {
                category.MegaMenuIlustrationBlockList = shopContentExpanded.MegaMenuIlustrationBlockList;
            }
        }

        // CMS MegaMenu config — attach mode + this category's own CustomListOfLinks /
        // ContentTile.Illustrations (now scoped per MegaMenu entry, not page-level).
        const matchingMegaMenu = megaMenuConfigs.find(m => m.categoryId === categoryId);
        if (matchingMegaMenu && matchingMegaMenu.listOfLinksSource) {
            category.cmsMegaMenuMode = matchingMegaMenu.listOfLinksSource; // 'FromSfcc' | 'CustomLinks' | 'IllustrationsOnly' | 'NoMenu'
            category.cmsCustomListOfLinks = matchingMegaMenu.customListOfLinks || [];
            category.cmsContentTileIllustrations = matchingMegaMenu.contentTileIllustrations || [];
        }
    };

    if (merged.id) {
        mergeToCategory(merged);
    }

    if (Array.isArray(merged.categories)) {
        merged.categories.forEach(categoryGroup => {
            if (categoryGroup.id) {
                mergeToCategory(categoryGroup);
            }

            if (Array.isArray(categoryGroup.categories)) {
                categoryGroup.categories.forEach(subCategory => {
                    if (subCategory.id) {
                        mergeToCategory(subCategory);
                    }
                });
            }
        });
    }

    return merged;
}

/**
 * Apply CategoryAddReplace entries (Prepend / Append / Replace) to a list of
 * first-level navigation categories for a given view type.
 *
 * Returns a mixed array of "items" — either a category passthrough or a CMS
 * CTA insertion — preserving order. Consumers detect the shape via the
 * `__cmsItemType` discriminator:
 *
 *   { __cmsItemType: 'category', category }
 *   { __cmsItemType: 'cta', block, key }
 *
 * The `block` is the expanded GDI block (typically a GDICtaBlock) ready to be
 * rendered via the shared GDI content renderer.
 *
 * @param {Array<Object>} categories - First-level navigation categories.
 * @param {Array<Object>} addReplaceEntries - Entries from header data.
 * @param {'WebMobile'|'WebTablet'|'WebDesktop'} currentViewType - Active layout.
 * @param {Array<Object>} [lookupCategories] - Full first-level category list used
 *   to resolve an entry's `showSubcategoriesCategoryId`. Defaults to `categories`.
 *   Pass the unsliced list when `categories` is a truncated view (e.g. the
 *   desktop bar shows only the first seven items) so a referenced category
 *   outside that window can still be located.
 * @returns {Array<Object>} Mixed list of category + CTA items.
 */

/**
 * Depth-first search for a category by id within a navigation tree, descending
 * through each node's `categories` children. Returns the first matching node
 * (at any depth) or null. Used to resolve `showSubcategoriesCategoryId` against
 * an arbitrary category — not just top-level (L1) entries.
 *
 * @param {Array<Object>} pool - Category nodes to search (each may have `categories`).
 * @param {string} categoryId - Target category id.
 * @returns {Object|null} Matching category node, or null.
 */
export const findCategoryByIdDeep = (pool, categoryId) => {
    if (!Array.isArray(pool) || !categoryId) return null;
    for (const category of pool) {
        if (!category) continue;
        if (category.id === categoryId) return category;
        const match = findCategoryByIdDeep(category.categories, categoryId);
        if (match) return match;
    }
    return null;
};

export const applyCategoryAddReplace = (
    categories,
    addReplaceEntries,
    currentViewType,
    lookupCategories
) => {
    const safeCategories = Array.isArray(categories) ? categories : [];
    const showSubcategoriesPool = Array.isArray(lookupCategories)
        ? lookupCategories
        : safeCategories;
    const result = safeCategories.map((c) => ({
        __cmsItemType: 'category',
        category: c,
        key: `cat-${c?.id ?? Math.random()}`
    }));

    if (!Array.isArray(addReplaceEntries) || !currentViewType) return result;

    const entries = addReplaceEntries.filter((e) => {
        if (!e || !e.viewTypeToApply || !e.categoryId || !e.action) return false;
        // `viewTypeToApply` may be a comma-separated list (e.g. "WebMobile,WebTablet,WebDesktop").
        // Match if the current view type is one of the authored tokens.
        const tokens = String(e.viewTypeToApply)
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean);
        return tokens.includes(currentViewType);
    });

    /* Find the slot for this entry's target category. Once a `Replace` has
     * fired, the original category item is gone from `result`, so subsequent
     * Prepend/Append entries targeting the same `categoryId` must also be
     * able to locate their slot via the CTA items left behind by that
     * Replace. We tag each inserted CTA with `sourceCategoryId` and match
     * on either shape here. */
    const matchesTarget = (item, categoryId) =>
        (item.__cmsItemType === 'category' && item.category?.id === categoryId) ||
        (item.__cmsItemType === 'cta' && item.sourceCategoryId === categoryId);

    entries.forEach((entry) => {
        const firstIndex = result.findIndex((item) => matchesTarget(item, entry.categoryId));
        if (firstIndex === -1) return;

        // Extend through any contiguous items belonging to the same target so
        // a re-Replace clears the previous insertion and Append lands after
        // the last CTA from a prior Replace (not in the middle of the group).
        let lastIndex = firstIndex;
        while (
            lastIndex + 1 < result.length &&
            matchesTarget(result[lastIndex + 1], entry.categoryId)
        ) {
            lastIndex += 1;
        }

        // `showSubcategoriesCategoryId` (CMS GDIGlobalComponentsPageShowSubcategories)
        // explicitly names the category whose navigation content (desktop
        // popover / mobile drill-in) the inserted CTA should surface. When
        // authored it wins for EVERY action — including Prepend/Append — and
        // points at an arbitrary category resolved from the full nav tree
        // (any depth, not just L1).
        //
        // The pre-loaded nav tree is intentionally shallow (L1 only), so a
        // referenced subcategory (L2+) usually is NOT present in the pool. In
        // that case we still return an id-only stub flagged with
        // `__cmsSubcategoriesRef` so consumers open the menu and fetch that
        // category's subcategories on demand by id (same mechanism the normal
        // L1 popover/drill-in uses) instead of silently rendering nothing.
        const explicitFallbackCategory = entry.showSubcategoriesCategoryId
            ? findCategoryByIdDeep(
                  showSubcategoriesPool,
                  entry.showSubcategoriesCategoryId
              ) || {
                  id: entry.showSubcategoriesCategoryId,
                  __cmsSubcategoriesRef: true
              }
            : null;

        // `fallbackCategory` powers the CTA's popover-fallback behaviour in
        // the consumer (renders the original category's submenu on hover).
        // Without an explicit reference we only attach it for `Replace` —
        // that's the only action that takes the category's slot in the nav,
        // so the submenu must move WITH the CTA. `Prepend`/`Append` are
        // siblings of the original category, which still renders alongside
        // them with its own popover; giving those CTAs a fallback would
        // render a duplicate popover (or worse, an empty one when the
        // category has already been Replaced and the CTA targets the same id
        // afterwards).
        const fallbackCategory =
            explicitFallbackCategory ||
            (entry.action === 'Replace'
                ? result
                      .slice(firstIndex, lastIndex + 1)
                      .find((item) => item.__cmsItemType === 'category')?.category || null
                : null);

        const ctaItems = (entry.replaceBy || [])
            .map((cb, i) => {
                const expanded = cb?.ContentLink?.Expanded;
                if (!expanded) return null;
                const guid = expanded.ContentLink?.GuidValue || `idx-${i}`;
                return {
                    __cmsItemType: 'cta',
                    block: expanded,
                    fallbackCategory,
                    sourceCategoryId: entry.categoryId,
                    key: `cta-${entry.categoryId}-${entry.action}-${guid}`
                };
            })
            .filter(Boolean);

        switch (entry.action) {
            case 'Prepend':
                if (ctaItems.length === 0) return;
                result.splice(firstIndex, 0, ...ctaItems);
                break;
            case 'Append':
                if (ctaItems.length === 0) return;
                result.splice(lastIndex + 1, 0, ...ctaItems);
                break;
            case 'Replace':
                result.splice(firstIndex, lastIndex - firstIndex + 1, ...ctaItems);
                break;
            case 'Remove':
                // Drop the target category (and any contiguous CTAs left behind
                // by a prior Replace targeting the same id) from the nav.
                result.splice(firstIndex, lastIndex - firstIndex + 1);
                break;
            default:
                break;
        }
    });

    return result;
}

/**
 * Inspect a CMS block from a CategoryAddReplace ReplaceBy entry and, when it is
 * a GDICtaBlock whose authored Style is "Mega Menu Link" (or empty), return the
 * data needed to render it inline as a plain navigation link — letting consumers
 * preserve the nav's own spacing/animation/styling. Returns null for blocks that
 * should keep their own GDICtaBlock styling.
 *
 * Also resolves any CMS-authored per-view-type style overrides (Color,
 * BackgroundColor, BorderColor, MinWidth, Padding, Margin) so the consumer can
 * spread them as Chakra props on top of the nav defaults.
 *
 * @param {Object} block - Expanded CMS block.
 * @param {'WebMobile'|'WebTablet'|'WebDesktop'} [currentViewType] - Active layout
 *   used to pick the matching per-view-type CtaText. Falls back to the first
 *   authored CtaText when omitted or when no entry targets the view type.
 * @returns {null | {text:string, ariaLabel:string|null, linkBlock:Object|null, styleProps:Object, hoverStyleProps:Object}}
 */
export const getMegaMenuLinkCtaData = (block, currentViewType) => {
    if (!block || block.__typename !== 'GDICtaBlock') return null;
    const stylesList = block.GDICtaBlockStylesPerViewType;
    const style = getPrimaryCtaStyleEntry(stylesList)?.Style;
    if (style && style !== 'Mega Menu Link') return null;
    const ctaText = resolveCtaTextForViewType(stylesList, currentViewType);
    const linkBlock = block.GDICtaBlockLink?.[0]?.ContentLink?.Expanded || null;
    const text = ctaText || linkBlock?.AriaLabel || linkBlock?.Name || '';
    const {styles: styleProps = {}, hoverStyles: hoverStyleProps = {}} =
        resolveGDIStyles(stylesList, MEGA_MENU_LINK_STYLE_MAP) || {};
    /* Second resolve pass for FontStyle — same pattern as gdiCtaBlock.
     * `MEGA_MENU_LINK_STYLE_MAP` intentionally omits FontStyle because the
     * authored token (e.g. `bold`, `italic`, `bold italic`) can map to
     * multiple Chakra props at once. Routing it through
     * `resolveResponsiveFontStyleProps` expands the responsive shape
     * into separate `fontWeight` / `fontStyle` / etc. props that override
     * the nav default (`textStyle: labelXs` → `fontWeight: medium`). */
    const {styles: fontStyleStyles = {}, hoverStyles: fontStyleHoverStyles = {}} =
        resolveGDIStyles(stylesList, {FontStyle: 'fontStyle'}) || {};
    Object.assign(styleProps, resolveResponsiveFontStyleProps(fontStyleStyles.fontStyle));
    Object.assign(hoverStyleProps, resolveFontStyleProps(fontStyleHoverStyles.fontStyle));
    return {
        text,
        ariaLabel: linkBlock?.AriaLabel || null,
        linkBlock,
        styleProps,
        hoverStyleProps
    };
}

/**
 * Transforms a MegaMenuItemLevelOneBlock CMS structure into a category-like structure
 * that can be rendered by the mega menu component.
 *
 * @param {Object} cmsMegaMenuData - The MegaMenuItemLevelOneBlock data from CMS
 * @param {Object} apiCategory - The original API category (for fallback data like c_relativeUrl)
 * @returns {Array} Array of category-like objects representing the menu columns
 */
export const transformMegaMenuBlockToCategories = (cmsMegaMenuData) => {
    if (!cmsMegaMenuData?.MegaMenuItemsLevelOne) {
        return [];
    }

    const columns = [];
    let illustrationBlocks = [];

    // Check for MegaMenuIlustrationBlockList directly on the MegaMenuItemLevelOneBlock
    // This is where illustration blocks are configured at the parent level
    if (cmsMegaMenuData.MegaMenuIlustrationBlockList?.length > 0) {
        illustrationBlocks = cmsMegaMenuData.MegaMenuIlustrationBlockList;
    }

    // MegaMenuItemsLevelOne contains MegaMenuItemLevelTwoBlock entries (columns)
    // It may also contain MegaMenuIlustrationBlock as siblings (check both locations)
    cmsMegaMenuData.MegaMenuItemsLevelOne.forEach((levelOneItem) => {
        const expandedBlock = levelOneItem.ContentLink?.Expanded;
        if (!expandedBlock) return;

        // Check if this is an illustration block (sibling to columns in the items array)
        if (expandedBlock.__typename === 'MegaMenuIlustrationBlock') {
            // Only add if not already found in MegaMenuIlustrationBlockList
            if (illustrationBlocks.length === 0) {
                illustrationBlocks.push(levelOneItem);
            }
            return;
        }

        // Otherwise, it should be a MegaMenuItemLevelTwoBlock (a column)
        if (expandedBlock.__typename !== 'MegaMenuItemLevelTwoBlock') {
            return;
        }

        const levelTwoBlock = expandedBlock;
        const columnCategoryId = levelTwoBlock.CategoryId?.trim();
        const columnItems = [];

        // Each item in MegaMenuItemsLevelTwo is a CustomCategoryBlock (menu link)
        if (levelTwoBlock.MegaMenuItemsLevelTwo) {
            levelTwoBlock.MegaMenuItemsLevelTwo.forEach((levelTwoItem) => {
                const customCategoryBlock = levelTwoItem.ContentLink?.Expanded;
                if (!customCategoryBlock) return;

                const itemCategoryId = customCategoryBlock.CategoryId?.trim();

                columnItems.push({
                    id: itemCategoryId,
                    name: customCategoryBlock.OverrideName || null,
                    CategoryReplacement: levelTwoItem, // Keep reference for styling
                    cmsCategoryData: customCategoryBlock, // Direct access to CMS data
                    c_relativeUrl: null, // Will be filled from API if needed
                    parentCategoryId: columnCategoryId
                });
            });
        }

        columns.push({
            id: columnCategoryId,
            name: levelTwoBlock.ColumnTitle || null, // Column header from CMS, fallback to API name
            c_showOnSeparateColumn: true,
            categories: columnItems,
            parentCategoryId: cmsMegaMenuData.CategoryId,
            CategoryReplacement: levelOneItem
        });
    });

    // Attach collected illustration blocks to the columns array for access in the component
    columns.illustrationBlocksFromParent = illustrationBlocks;

    return columns;
};

/**
* Retrieves the value of a specified attribute from a category within a category tree by category ID.
*
* Traverses the category tree recursively to find the category with the given ID and returns the value
* of the requested attribute if it exists. Returns null if the category or attribute is not found.
*
* @param {Object} categoryTree - The root of the category tree, with possible nested categories.
* @param {string|number} categoryId - The unique identifier of the category to search for.
* @param {string} attributeName - The name of the attribute to retrieve from the found category.
* @returns {*} The value of the specified attribute if found, otherwise null.
*/
const getCategoryAttribute = (categoryTree, categoryId, attributeName) => {
    if (!categoryTree || !categoryId || !attributeName) return null;

    if (categoryTree.id === categoryId) {
        return categoryTree[attributeName] || null;
    }

    const searchCategories = (categories) => {
        if (!Array.isArray(categories)) return null;

        for (const category of categories) {
            if (category.id === categoryId) {
                return category[attributeName] || null;
            }

            if (Array.isArray(category.categories)) {
                const result = searchCategories(category.categories);
                if (result !== null) return result;
            }
        }

        return null;
    };

    if (Array.isArray(categoryTree.categories)) {
        return searchCategories(categoryTree.categories);
    }

    return null;
}

/**
 * Merges category names from source data into target data based on matching IDs
 * @param {Object} sourceData - The first object containing complete category information
 * @param {Array} targetData - The second object array to be updated
 * @returns {Array} Updated target data with names filled in from source
 */
export const mergeCategoryNames = (sourceData, targetData) => {
    // Create a map of all categories from source data for quick lookup
    const categoryMap = new Map();

    const buildCategoryMap = (category) => {
        categoryMap.set(category.id, category.name);
        if (category.categories && Array.isArray(category.categories)) {
            category.categories.forEach(buildCategoryMap);
        }
    };

    // Build the map from source data
    if (sourceData.categories && Array.isArray(sourceData.categories)) {
        sourceData.categories.forEach(buildCategoryMap);
    }
    buildCategoryMap(sourceData);

    // Update target data with names from the map
    const updateTargetData = (targetCategory) => {
        // If name is null or missing, try to get it from the map
        if (!targetCategory.name && categoryMap.has(targetCategory.id)) {
            targetCategory.name = categoryMap.get(targetCategory.id);
        }

        // Process subcategories recursively
        if (targetCategory.categories && Array.isArray(targetCategory.categories)) {
            targetCategory.categories.forEach(updateTargetData);
        }

        // Process parentCategoryTree
        if (targetCategory.parentCategoryTree && Array.isArray(targetCategory.parentCategoryTree)) {
            targetCategory.parentCategoryTree.forEach((tree) => {
                if (!tree.name && categoryMap.has(tree.id)) {
                    tree.name = categoryMap.get(tree.id);
                }
            });
        }
    };

    // Update each top-level category in target
    targetData.forEach((targetCategory) => {
        updateTargetData(targetCategory);
    });

    return targetData;
}

/**
* Transforms CMS menu data into a structured category object suitable for mega menu rendering.
*
* @param {Array<Object>} cmsData - The CMS menu data array, where each item contains nested ContentLink and Expanded properties.
* @param {Object} category - The root category object, used to retrieve category attributes such as name.
* @returns {Object} A structured object representing the root category, including its id, name, MegaMenuIlustrationBlockList, and a nested categories array with level one and level two categories.
*/
export const transformCMSMenuToCategories = (cmsData, apiData) => {
    if (!Array.isArray(cmsData) || cmsData.length === 0) {
        return []
    }
    const category = apiData?.category
    const cmsDataRoot = cmsData[0]?.ContentLink?.Expanded

    const result = {
        id: cmsDataRoot?.CategoryId,
        name: getCategoryAttribute(category, category.id, 'name'),
        MegaMenuIlustrationBlockList: cmsDataRoot?.MegaMenuIlustrationBlockList,
        parentCategoryId: 'root_gar_ca',
        onlineSubCategoriesCount: cmsDataRoot?.MegaMenuItemsLevelOne?.length,
        parentCategoryTree: [{
            id: 'root_gar_ca',
            name: 'g'
        }, {
            id: cmsDataRoot?.CategoryId,
            name: getCategoryAttribute(category, cmsDataRoot?.CategoryId, 'name')
        }],
        categories: []
    }

    cmsData.forEach((menuItem) => {
        const levelOne = menuItem?.ContentLink?.Expanded?.MegaMenuItemsLevelOne

        if (Array.isArray(levelOne)) {
            levelOne.forEach((l1Item) => {
                const l1Expanded = l1Item?.ContentLink?.Expanded
                const l1CategoryId = l1Expanded?.CategoryId?.trim()

                if (l1CategoryId) {
                    const levelTwo = l1Expanded?.MegaMenuItemsLevelTwo

                    const categoryObj = {
                        CategoryReplacement: l1Item,
                        id: l1CategoryId,
                        name: l1Expanded?.OverrideName || getCategoryAttribute(category, l1CategoryId, 'name'),
                        c_showOnSeparateColumn: true,
                        parentCategoryId: cmsDataRoot?.CategoryId,
                        parentCategoryTree: [{
                            id: 'root_gar_ca',
                            name: 'g'
                        }, {
                            id: cmsDataRoot?.CategoryId,
                            name: l1Expanded?.OverrideName || getCategoryAttribute(category, cmsDataRoot?.CategoryId, 'name')
                        }],
                        onlineSubCategoriesCount: levelTwo?.length,
                        c_relativeUrl: getCategoryAttribute(category, l1CategoryId, 'c_relativeUrl'),
                        categories: []
                    }

                    if (Array.isArray(levelTwo)) {
                        levelTwo.forEach((l2Item) => {
                            const l2Expanded = l2Item?.ContentLink?.Expanded
                            const l2CategoryId = l2Expanded?.CategoryId?.trim()

                            if (l2CategoryId) {
                                categoryObj.categories.push({
                                    CategoryReplacement: l2Item,
                                    id: l2CategoryId,
                                    name: l2Expanded?.OverrideName || getCategoryAttribute(category, l2CategoryId, 'name'),
                                    c_relativeUrl: getCategoryAttribute(category, l2CategoryId, 'c_relativeUrl'),
                                    parentCategoryId: l1CategoryId,
                                    parentCategoryTree: [{
                                        id: 'root_gar_ca',
                                        name: 'g'
                                    }, {
                                        id: cmsDataRoot?.CategoryId,
                                        name: getCategoryAttribute(category, cmsDataRoot?.CategoryId, 'name')
                                    },
                                    {
                                        id: l1CategoryId,
                                        name: l1Expanded?.OverrideName || getCategoryAttribute(category, l1CategoryId, 'name')
                                    }]
                                })
                            }
                        })
                    }

                    result.categories.push(categoryObj)
                }
            })
        }
    })

    return result
}

/**
* Generates style and loading properties to conditionally hide a component on specified device types.
*
* @param {string} hideOnDevices - A comma-separated string of device names ('Mobile', 'Tablet', 'Desktop') on which the component should be hidden.
* @returns {Object} An object containing:
*   - display: An object with responsive display properties (base, md, lg) set to 'none' or 'block' based on the devices to hide.
*   - doNotLoadComponent: {boolean} Indicates whether the component should be completely omitted from rendering (true if hidden on all devices).
*/
export const getHideStyles = (hideOnDevices) => {
    if (!hideOnDevices) return {}

    const devices = hideOnDevices.split(',').map(d => d.trim())
    const display = {}
    let doNotLoadComponent = false

    if (devices.includes('Mobile')) {
        display.base = 'none'
        display.md = 'block'
        display.lg = 'block'
    }

    if (devices.includes('Tablet')) {
        display.md = 'none'
        display.lg = 'block'
    }

    if (devices.includes('Desktop')) {
        display.lg = 'none'
    }

    if (devices.includes('Mobile') && devices.includes('Tablet') && devices.includes('Desktop')) {
        doNotLoadComponent = true
    }

    // Only return display property if it has values (prevents SSR issues with empty objects)
    const result = {}
    if (Object.keys(display).length > 0) {
        result.display = display
    }
    if (doNotLoadComponent) {
        result.doNotLoadComponent = doNotLoadComponent
    }

    return result
}

/**
 * Builds a full canonical URL by concatenating the base URL with a relative path.
 *
 * @param {string} relativePath - The relative path from CMS (e.g., "/faq", "/privacy-policy")
 * @param {string} baseUrl - The base URL/origin (e.g., "https://www.garageclothing.co.uk")
 * @returns {string|null} The full canonical URL or null if no path provided
 */
const buildCanonicalUrl = (relativePath, baseUrl) => {
    if (!relativePath) return null
    if (!baseUrl) return relativePath

    let cleanBase = baseUrl.replace(/\/$/, '')
    if (!cleanBase.startsWith('http')) {
        cleanBase = `https://${cleanBase}`
    }
    const cleanPath = relativePath.startsWith('/') ? relativePath : `/${relativePath}`

    return `${cleanBase}${cleanPath}`
}

/**
 * Extracts SEO fields from Optimizely CMS page data.
 *
 * @param {Object} pageData - The CMS page data object (e.g., StandardPage, LandingPage, WebContentPage)
 * @param {string} baseUrl - The base URL/origin to prepend to canonical URL
 * @returns {Object} SEO data object with the following properties:
 *   - metaTitle: {string|null} - Page meta title
 *   - metaDescription: {string|null} - Page meta description
 *   - canonicalUrl: {string|null} - Full canonical URL for the page (baseUrl + CMS path)
 *   - excludeFromSitemap: {boolean} - Whether to exclude from sitemap
 *   - openGraphTags: {Array} - Array of OpenGraph tag objects with Property and Content fields
 *   - noIndex: {boolean} - Whether to add noindex robots tag (derived from excludeFromSitemap)
 */
export const extractSeoFromCmsPage = (pageData, baseUrl = '') => {
    if (!pageData) {
        return {
            metaTitle: null,
            metaDescription: null,
            canonicalUrl: null,
            excludeFromSitemap: false,
            openGraphTags: [],
            noIndex: false
        }
    }

    // SEO fields from Optimizely CMS SEO Property Group
    const metaTitle = pageData.MetaTitle || null
    const metaDescription = pageData.MetaDescription || null
    const excludeFromSitemap = pageData.ExcludeFromSitemap === true

    // Build full canonical URL by concatenating baseUrl with CMS path
    const canonicalUrl = buildCanonicalUrl(pageData.CanonicalUrl, baseUrl)

    // OpenGraphTags is an OpenGraphListProperty - array of objects with Property and Content
    const openGraphTags = Array.isArray(pageData.OpenGraphTags)
        ? pageData.OpenGraphTags
        : []

    return {
        metaTitle,
        metaDescription,
        canonicalUrl,
        excludeFromSitemap,
        openGraphTags,
        // If excluded from sitemap, typically should also be noindex
        noIndex: excludeFromSitemap
    }
}

/**
 * Transforms OpenGraph tags array from CMS into React Helmet compatible meta tags.
 *
 * @param {Array} openGraphTags - Array of OpenGraph tag objects with Property and Content fields
 * @returns {Array} Array of meta tag objects for React Helmet { property, content }
 */
export const transformOpenGraphTags = (openGraphTags) => {
    if (!Array.isArray(openGraphTags) || openGraphTags.length === 0) {
        return []
    }

    return openGraphTags
        .filter(tag => tag?.Property && tag?.Content)
        .map(tag => ({
            property: tag.Property,
            content: tag.Content
        }))
}

/**
 * Maps a CMS `GDITextBlockStylesPerViewType` array to a Chakra-compatible responsive `sx` object.
 *
 * Each entry in `stylesPerViewType` targets a viewport via `ViewTypesToApply`:
 *   "WebMobile"  → `base`
 *   "WebTablet"  → `md`
 *   "WebDesktop" → `lg`
 *
 * @param {Array} stylesPerViewType - Array of CMS style entries from `GDITextBlockStylesPerViewType`
 * @returns {Object} Chakra responsive sx object
 */
export const buildViewTypeStyles = (stylesPerViewType) => {
    if (!Array.isArray(stylesPerViewType) || stylesPerViewType.length === 0) return {}
    const allBreakpoints = ['base', 'md', 'lg']
    const isWebBreakpoint = (bp) => allBreakpoints.includes(bp)
    const result = {}
    const add = (prop, val, bp) => {
        const value = typeof val === 'string' ? val.trim() : val
        if (!value || value === 'Default / Inherit') return
        if (!result[prop]) result[prop] = {}
        result[prop][bp] = value
    }
    const applyEntry = ({TextAlignment, TextColor, BackgroundColor, FontSize, TextMaxWidth, Paddings, Margins}, breakpoints) => {
        breakpoints.forEach((bp) => {
            add('textAlign', TextAlignment?.toLowerCase(), bp)
            add('color', TextColor, bp)
            add('backgroundColor', BackgroundColor, bp)
            add('fontSize', FontSize, bp)
            add('maxWidth', TextMaxWidth, bp)
            add('padding', Paddings, bp)
            add('margin', Margins, bp)
        })
    }

    stylesPerViewType
        .filter((entry) => !hasGDIViewTypes(entry))
        .forEach((entry) => applyEntry(entry, allBreakpoints))

    stylesPerViewType
        .filter((entry) => hasGDIViewTypes(entry))
        .forEach((entry) =>
            applyEntry(entry, parseGDIViewTypes(entry.ViewTypesToApply).filter(isWebBreakpoint))
        )

    Object.keys(result).forEach((prop) => {
        result[prop] = fillGDIResponsiveInheritance(result[prop])
    })

    return result
}
